<template>
  <div class="w-full h-full max-w-xl bg-white">
    <!-- Client Info -->
    <div class="mb-6">
      <h3 class="font-semibold mb-2">Client Info</h3>
      <table class="min-w-full border border-gray-200 rounded">
        <tbody>
            <tr v-for="item in clientFields" :key="item.key">
              <td class="font-semibold px-4 py-2 border-b">{{ item.label }}</td>
              <td class="px-4 py-2 border-b">{{ item.value }}</td>
            </tr>
        </tbody>
      </table>
    </div>
    <!-- Subscription Plan -->
    <div class="mb-6">
      <h3 class="font-semibold mb-2">Subscription Plan</h3>
      <div class="flex flex-col sm:flex-row sm:items-center justify-between border-b pb-2 mb-2 p-3 rounded-lg border bg-white shadow-sm">
        <div class="flex items-center gap-3">
          <div v-if="selectedPlanObj.product?.images?.[0]" class="flex-shrink-0">
            <img :src="selectedPlanObj.product.images[0]" :alt="selectedPlanObj.product.name || 'Plan' + ' image'" class="h-10 w-10 object-contain rounded" />
          </div>
          <div>
            <div class="font-medium">{{ selectedPlanObj.product?.name || 'N/A' }}</div>
            <div class="text-xs text-gray-500">{{ selectedPlanObj.product?.description || '' }}</div>
          </div>
        </div>
        <div class="flex flex-col sm:flex-row sm:items-center gap-2 mt-2 sm:mt-0">
          <span class="text-xs font-semibold text-blue-700">
            {{ formatCurrencyCents(selectedPlanObj.unit_amount, selectedPlanObj.currency) }}
          </span>
          <span v-if="selectedPlanObj.recurring" class="text-xs text-gray-400 ml-1">
            / {{ selectedPlanObj.recurring.interval }}
          </span>
        </div>
      </div>
    </div>
    <!-- Actions -->
    <div class="flex flex-col sm:flex-row justify-between">
      <button
        type="button"
        class="bg-gray-200 px-6 py-2 rounded w-full mb-2 sm:mb-0 sm:w-auto sm:mr-3"
        @click="$emit('prev')"
        :disabled="loading"
      >
        Back
      </button>
      <button
        class="btn btn-blue w-full sm:w-auto flex items-center justify-center"
        :disabled="loading"
        @click="redirectToCheckout"
      >
        <span v-if="loading" class="loader mr-2"></span>
        {{ loading ? 'Redirecting...' : 'Proceed for payment' }}
      </button>
    </div>
    <div v-if="checkoutError" class="text-red-600 w-full text-right mt-2">{{ checkoutError }}</div>
  </div>
</template>

<script>
import { loadStripe } from '@stripe/stripe-js';
import axios from 'axios';
import { formatCellNumber, formatCurrencyCents } from '../../utils/formatters';

export default {
  props: {
    client: Object,
    user: Object,
    plans: Array,
    selectedPlan: [String, Number],
    states: { type: Array, default: () => [] },
    countries: { type: Array, default: () => [] }
  },
  data() {
    return {
      loading: false,
      checkoutError: '',
      localStates: [],
      localCountries: [],
    };
  },
  computed: {
    selectedPlanObj() {
      return this.plans.find(p => p.id === this.selectedPlan) || {};
    },
    stateName() {
      const code = this.client?.state;
      const states = this.states.length ? this.states : this.localStates;
      return states.find(s => s.state === code)?.name || code || '';
    },
    countryName() {
      const id = this.client?.country_id;
      const countries = this.countries.length ? this.countries : this.localCountries;
      return countries.find(c => c.id == id)?.name || '';
    },
    clientFields() {
      return [
        { key: 'name', label: 'Name', value: this.client?.name || '' },
        { key: 'email', label: 'Email', value: this.user?.email || '' },
        { key: 'cell', label: 'Cell', value: this.formatCellNumber(this.user?.cell) || '' },
        { key: 'address', label: 'Address', value: this.client?.address || '' },
        { key: 'city', label: 'City', value: this.client?.city || '' },
        { key: 'state', label: 'State', value: this.stateName },
        { key: 'country', label: 'Country', value: this.countryName },
        { key: 'postal_code', label: 'Postal Code', value: this.client?.postal_code || '' },
      ];
    },
  },
  mounted() {
    if (!this.states.length || !this.countries.length) {
      axios.get('/api/address-info').then(({data}) => {
        this.localStates = data.states || [];
        this.localCountries = data.countries || [];
      });
    }
  },
  methods: {
    formatCurrencyCents,
    formatCellNumber,
    async redirectToCheckout() {
      if (this.loading) return;
      this.loading = true;
      this.checkoutError = '';
      try {
        if (!this.client?.id || !this.user?.id) {
          this.checkoutError = 'Client or user information is missing.';
          return;
        }
        const items = [{ price: this.selectedPlan, quantity: 1 }];
        const { data } = await axios.post('/create-checkout-session', {
          client_id: this.client.id,
          user_id: this.user.id,
          items
        });
        const stripe = await loadStripe(window.STRIPE_PUBLISHABLE_KEY);
        await stripe.redirectToCheckout({ sessionId: data.id });
      } catch (err) {
        this.checkoutError = err.response?.data?.error || 'Could not start checkout.';
      } finally {
        this.loading = false;
      }
    }
  }
}
</script>

<style>
/* Optional: Loader style, can reuse your existing spinner class */
.loader {
  border: 2px solid #fff;
  border-top: 2px solid #2563eb;
  border-radius: 50%;
  width: 1rem;
  height: 1rem;
  animation: spin 0.8s linear infinite;
  display: inline-block;
  vertical-align: middle;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
